import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { AppDispatch } from "../../../redux/store";
import { fetchRoleplay } from "../../../redux/actions/roleplayActions";

interface RoleplayAssessedSessionViewProps {
  currentAssessedSession: any;
}

const AssessedSessionRoleplayView: React.FC<
  RoleplayAssessedSessionViewProps
> = ({ currentAssessedSession }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [roleplayData, setRoleplayData] = useState<any>(null);
  const [loadingRoleplay, setLoadingRoleplay] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Handle copy to clipboard
  const handleCopyInferenceId = async (inferenceId: string) => {
    try {
      await navigator.clipboard.writeText(inferenceId);
      setCopiedId(inferenceId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy inference ID:", err);
    }
  };

  const roleplayAnalysis = currentAssessedSession.roleplay_analysis;
  const concernScores = roleplayAnalysis?.concern_scores || {};
  const overallScore = currentAssessedSession.assessment_score;

  // Fetch roleplay data to get concern details
  useEffect(() => {
    const fetchRoleplayData = async () => {
      if (
        currentAssessedSession.source_roleplay_id &&
        currentAssessedSession.organization_id
      ) {
        setLoadingRoleplay(true);
        try {
          const result = await dispatch(
            fetchRoleplay({
              roleplayId: currentAssessedSession.source_roleplay_id,
              organizationId: currentAssessedSession.organization_id,
            })
          ).unwrap();
          setRoleplayData(result);
        } catch (error) {
          console.error("Failed to fetch roleplay data:", error);
        } finally {
          setLoadingRoleplay(false);
        }
      }
    };
    fetchRoleplayData();
  }, [
    dispatch,
    currentAssessedSession.source_roleplay_id,
    currentAssessedSession.organization_id,
  ]);

  // Create maps for concern details
  const concernDetailsMap = useMemo(() => {
    const map: { [key: string]: { title: string; question: string } } = {};
    if (roleplayData?.generated_concerns) {
      roleplayData.generated_concerns.forEach((concern: any) => {
        map[concern.id] = {
          title: concern.title || concern.id,
          question: concern.question || "",
        };
      });
    }
    return map;
  }, [roleplayData]);

  // Calculate statistics
  const totalConcerns = Object.keys(concernScores).length;
  const averageScore =
    totalConcerns > 0
      ? Object.values(concernScores).reduce(
          (sum: number, score: any) => sum + score.score,
          0
        ) / totalConcerns
      : 0;

  // Get score color based on value (1-10 scale)
  const getScoreColor = (score: number) => {
    if (score >= 7) return "success";
    if (score >= 4) return "warning";
    return "error";
  };

  // Get score percentage for progress bar (1-10 scale to 0-100%)
  const getScorePercentage = (score: number) => {
    return ((score - 1) / 9) * 100;
  };

  return (
    <Box>
      {/* Overall Score Card */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <TrophyIcon
                    sx={{ fontSize: 48 }}
                    color={
                      overallScore ? getScoreColor(overallScore) : "action"
                    }
                  />
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Overall Performance
                    </Typography>
                    <Typography variant="h3" fontWeight="bold">
                      {overallScore ? overallScore.toFixed(1) : "N/A"}
                      <Typography
                        component="span"
                        variant="h5"
                        color="text.secondary"
                      >
                        /10
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography variant="body2" color="text.secondary">
                    Average Score
                  </Typography>
                  <Typography variant="h4" fontWeight="medium">
                    {averageScore.toFixed(1)}
                    <Typography
                      component="span"
                      variant="body1"
                      color="text.secondary"
                    >
                      /10
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on {totalConcerns} concern
                    {totalConcerns !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Concern Scores Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Concern Scores & Justifications
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {loadingRoleplay ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : totalConcerns === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No concern scores available for this session.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="20%">Title</TableCell>
                      <TableCell width="25%">Question</TableCell>
                      <TableCell width="12%" align="center">
                        Score
                      </TableCell>
                      <TableCell width="33%">Justification</TableCell>
                      <TableCell width="10%">Inference ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(concernScores).map(
                      ([concernId, scoreData]: [string, any]) => {
                        const concernDetails = concernDetailsMap[concernId] || {
                          title: concernId,
                          question: "",
                        };
                        return (
                          <TableRow key={concernId}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {concernDetails.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {concernDetails.question}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                gap={1}
                              >
                                <Chip
                                  label={`${scoreData.score.toFixed(1)}/10`}
                                  color={getScoreColor(scoreData.score)}
                                  size="small"
                                  icon={<StarIcon />}
                                />
                                <LinearProgress
                                  variant="determinate"
                                  value={getScorePercentage(scoreData.score)}
                                  color={getScoreColor(scoreData.score)}
                                  sx={{
                                    width: "100%",
                                    height: 6,
                                    borderRadius: 1,
                                  }}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {scoreData.justification ||
                                  "No justification provided"}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {scoreData.source_llm_inference_id ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: "0.7rem",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {scoreData.source_llm_inference_id.substring(
                                      0,
                                      8
                                    )}
                                    ...
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant={
                                      copiedId ===
                                      scoreData.source_llm_inference_id
                                        ? "contained"
                                        : "outlined"
                                    }
                                    color={
                                      copiedId ===
                                      scoreData.source_llm_inference_id
                                        ? "success"
                                        : "primary"
                                    }
                                    onClick={() =>
                                      handleCopyInferenceId(
                                        scoreData.source_llm_inference_id
                                      )
                                    }
                                    startIcon={<ContentCopyIcon />}
                                    sx={{ minWidth: "auto", px: 1 }}
                                  >
                                    {copiedId ===
                                    scoreData.source_llm_inference_id
                                      ? "Copied"
                                      : "Copy"}
                                  </Button>
                                </Box>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Link to Roleplay Scenario */}
        {currentAssessedSession.source_roleplay_id && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Source Roleplay Scenario
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace", mt: 0.5 }}
                    >
                      {currentAssessedSession.source_roleplay_id}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      navigate(
                        `/dashboard/roleplays/${currentAssessedSession.organization_id}/${currentAssessedSession.source_roleplay_id}`
                      )
                    }
                  >
                    View Roleplay
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Session Metadata */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Session Duration
              </Typography>
              <Typography variant="h6">
                {currentAssessedSession.duration_in_min
                  ? `${currentAssessedSession.duration_in_min.toFixed(
                      1
                    )} minutes`
                  : "N/A"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Processing Type
              </Typography>
              <Chip
                label="Roleplay"
                color="secondary"
                icon={<CheckCircleIcon />}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssessedSessionRoleplayView;
